// ── Installer Execution Strictness — Settings Card ──
// Three-mode platform setting (Velocity / Standard / Strict) with audit log.
// Editable by super_admin, owner, admin, ops_manager, head_of_installation.
// Layer 2: rolling check-in deviation log (last 50).
// Layer 3a: Proof Enforcement Policy selector + audit + Phase Submit Gap Log.

import { useRole } from "@/data/RoleContext";
import {
  useInstallerExecution,
  canEditInstallerStrictness,
  STRICTNESS_LABEL,
  STRICTNESS_DESCRIPTION,
  type InstallerStrictness,
} from "@/data/InstallerExecutionContext";
import {
  PROOF_POLICY_LABEL,
  PROOF_POLICY_DESCRIPTION,
  type ProofPolicy,
} from "@/data/installerProof";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Lock, Wrench, Shield, Zap, History, MapPin, Clock, AlertTriangle, Camera, ListChecks, FileCheck2 } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

const MODE_ORDER: InstallerStrictness[] = ["velocity", "standard", "strict"];

const MODE_VISUAL: Record<InstallerStrictness, { icon: typeof Zap; tone: string; ring: string }> = {
  velocity: { icon: Zap, tone: "text-info", ring: "ring-info/30 bg-info/10" },
  standard: { icon: Shield, tone: "text-primary", ring: "ring-primary/30 bg-primary/15" },
  strict: { icon: Lock, tone: "text-warning", ring: "ring-warning/30 bg-warning/10" },
};

export function InstallerExecutionSettings() {
  const { role } = useRole();
  const exec = useInstallerExecution();
  const canEdit = canEditInstallerStrictness(role);
  const [showLog, setShowLog] = useState(false);

  const handleSelect = (mode: InstallerStrictness) => {
    if (!canEdit) {
      toast.error("You don't have permission to change this setting.");
      return;
    }
    if (mode === exec.strictness) return;
    exec.setStrictness(mode, { changedBy: role });
    toast.success(`Installer execution mode set to ${STRICTNESS_LABEL[mode]}.`);
  };

  return (
    <div className="space-y-4">
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-4 w-4" />
              Installer Execution Strictness
            </CardTitle>
            <CardDescription>
              How strictly the field app enforces phase gates, post-submit edits, and geo deviations.
              Always-on: location permission required, every check-in captures coords + distance to job address.
            </CardDescription>
          </div>
          {!canEdit && (
            <Badge variant="outline" className="shrink-0 gap-1">
              <Lock className="h-3 w-3" /> Read only
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2 md:grid-cols-3">
          {MODE_ORDER.map((mode) => {
            const v = MODE_VISUAL[mode];
            const Icon = v.icon;
            const active = exec.strictness === mode;
            return (
              <button
                key={mode}
                type="button"
                disabled={!canEdit}
                onClick={() => handleSelect(mode)}
                className={[
                  "text-left rounded-lg border p-3 transition-all",
                  active
                    ? `ring-1 ${v.ring} border-transparent`
                    : "border-border hover:border-foreground/20 bg-card",
                  !canEdit && !active ? "opacity-60 cursor-not-allowed" : "",
                ].join(" ")}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Icon className={`h-4 w-4 ${active ? v.tone : "text-muted-foreground"}`} />
                  <span className={`text-sm font-bold ${active ? v.tone : ""}`}>
                    {STRICTNESS_LABEL[mode]}
                  </span>
                  {active && (
                    <Badge className={`ml-auto text-[9px] ${v.ring} ${v.tone} border-0`}>
                      Active
                    </Badge>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  {STRICTNESS_DESCRIPTION[mode]}
                </p>
              </button>
            );
          })}
        </div>

        {exec.auditLog.length > 0 && (
          <div className="pt-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowLog((s) => !s)}
              className="h-7 px-2 gap-1.5 text-[11px]"
            >
              <History className="h-3 w-3" />
              {showLog ? "Hide" : "Show"} change log ({exec.auditLog.length})
            </Button>
            {showLog && (
              <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto">
                {exec.auditLog.map((entry) => (
                  <div
                    key={entry.id}
                    className="text-[11px] flex items-center gap-2 px-2 py-1 rounded bg-muted/40"
                  >
                    <span className="text-muted-foreground tabular-nums shrink-0">
                      {new Date(entry.changedAt).toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </span>
                    <span className="text-muted-foreground">·</span>
                    <span className="font-medium">{entry.changedBy}</span>
                    <span className="text-muted-foreground">changed</span>
                    <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">
                      {STRICTNESS_LABEL[entry.from]}
                    </Badge>
                    <span className="text-muted-foreground">→</span>
                    <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">
                      {STRICTNESS_LABEL[entry.to]}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Layer 2: Check-in deviation log (5-col rolling, last 50) ── */}
        <CheckInDeviationLog />
      </CardContent>
    </Card>
    {/* ── Layer 3a: Proof Enforcement Policy ── */}
    <ProofEnforcementPolicyCard />
    </div>
  );
}

function CheckInDeviationLog() {
  const exec = useInstallerExecution();
  const [open, setOpen] = useState(false);
  const events = exec.deviationEvents;

  if (events.length === 0) return null;

  const highCount = events.filter((e) => e.highPriority).length;

  return (
    <div className="pt-2 border-t">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen((s) => !s)}
        className="h-7 px-2 gap-1.5 text-[11px]"
      >
        <AlertTriangle className="h-3 w-3" />
        {open ? "Hide" : "Show"} check-in deviations ({events.length}
        {highCount > 0 ? `, ${highCount} high` : ""})
      </Button>
      {open && (
        <div className="mt-2 rounded border overflow-hidden">
          <div className="grid grid-cols-[110px_1fr_72px_92px_1fr] gap-2 px-2 py-1.5 bg-muted/60 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <div>Time</div>
            <div>Job</div>
            <div>Type</div>
            <div>Value</div>
            <div>Reason</div>
          </div>
          <div className="max-h-64 overflow-y-auto divide-y">
            {events.map((ev) => (
              <div
                key={ev.id}
                className={[
                  "grid grid-cols-[110px_1fr_72px_92px_1fr] gap-2 px-2 py-1.5 text-[11px] items-center",
                  ev.highPriority ? "bg-warning/5" : "",
                ].join(" ")}
              >
                <span className="text-muted-foreground tabular-nums">
                  {new Date(ev.capturedAt).toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </span>
                <span className="font-medium truncate" title={ev.jobLabel}>
                  {ev.jobLabel}
                </span>
                <span className="flex items-center gap-1">
                  {ev.type === "time" ? (
                    <Clock className="h-3 w-3 text-muted-foreground" />
                  ) : (
                    <MapPin className="h-3 w-3 text-muted-foreground" />
                  )}
                  <span className="capitalize">{ev.type}</span>
                </span>
                <span className={`tabular-nums ${ev.highPriority ? "text-warning font-bold" : ""}`}>
                  {ev.valueLabel}
                </span>
                <span className="text-muted-foreground truncate" title={ev.reason ?? ""}>
                  {ev.reason ?? <span className="opacity-50">—</span>}
                </span>
              </div>
            ))}
          </div>
          {events.length >= 50 && (
            <div className="px-2 py-1 text-[10px] text-muted-foreground bg-muted/30 border-t">
              Showing last 50. Older entries roll off automatically.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   Layer 3a — Proof Enforcement Policy card
   Independent of Execution Mode. 4 modes, audit log, gap log.
   ───────────────────────────────────────────────────────────────────── */

const PROOF_ORDER: ProofPolicy[] = ["strict_proof", "checklist_only", "photo_only", "advisory"];

const PROOF_VISUAL: Record<ProofPolicy, { icon: typeof Zap; tone: string; ring: string }> = {
  strict_proof: { icon: Shield, tone: "text-primary", ring: "ring-primary/30 bg-primary/15" },
  checklist_only: { icon: ListChecks, tone: "text-info", ring: "ring-info/30 bg-info/10" },
  photo_only: { icon: Camera, tone: "text-info", ring: "ring-info/30 bg-info/10" },
  advisory: { icon: FileCheck2, tone: "text-warning", ring: "ring-warning/30 bg-warning/10" },
};

function ProofEnforcementPolicyCard() {
  const { role } = useRole();
  const exec = useInstallerExecution();
  const canEdit = canEditInstallerStrictness(role);
  const [showAudit, setShowAudit] = useState(false);

  const handleSelect = (mode: ProofPolicy) => {
    if (!canEdit) {
      toast.error("You don't have permission to change this setting.");
      return;
    }
    if (mode === exec.proofPolicy) return;
    exec.setProofPolicy(mode, { changedBy: role });
    toast.success(`Proof policy set to ${PROOF_POLICY_LABEL[mode]}.`);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileCheck2 className="h-4 w-4" />
              Proof Enforcement Policy
            </CardTitle>
            <CardDescription>
              How strictly photos and checklist items are enforced at phase submit.
              Independent of execution strictness. Hard rule — at least one photo
              or check is required to submit in every mode.
            </CardDescription>
          </div>
          {!canEdit && (
            <Badge variant="outline" className="shrink-0 gap-1">
              <Lock className="h-3 w-3" /> Read only
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2 md:grid-cols-2">
          {PROOF_ORDER.map((mode) => {
            const v = PROOF_VISUAL[mode];
            const Icon = v.icon;
            const active = exec.proofPolicy === mode;
            return (
              <button
                key={mode}
                type="button"
                disabled={!canEdit}
                onClick={() => handleSelect(mode)}
                className={[
                  "text-left rounded-lg border p-3 transition-all",
                  active
                    ? `ring-1 ${v.ring} border-transparent`
                    : "border-border hover:border-foreground/20 bg-card",
                  !canEdit && !active ? "opacity-60 cursor-not-allowed" : "",
                ].join(" ")}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Icon className={`h-4 w-4 ${active ? v.tone : "text-muted-foreground"}`} />
                  <span className={`text-sm font-bold ${active ? v.tone : ""}`}>
                    {PROOF_POLICY_LABEL[mode]}
                  </span>
                  {active && (
                    <Badge className={`ml-auto text-[9px] ${v.ring} ${v.tone} border-0`}>
                      Active
                    </Badge>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  {PROOF_POLICY_DESCRIPTION[mode]}
                </p>
              </button>
            );
          })}
        </div>

        {exec.proofPolicyAudit.length > 0 && (
          <div className="pt-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAudit((s) => !s)}
              className="h-7 px-2 gap-1.5 text-[11px]"
            >
              <History className="h-3 w-3" />
              {showAudit ? "Hide" : "Show"} policy change log ({exec.proofPolicyAudit.length})
            </Button>
            {showAudit && (
              <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto">
                {exec.proofPolicyAudit.map((entry) => (
                  <div
                    key={entry.id}
                    className="text-[11px] flex items-center gap-2 px-2 py-1 rounded bg-muted/40"
                  >
                    <span className="text-muted-foreground tabular-nums shrink-0">
                      {new Date(entry.changedAt).toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </span>
                    <span className="text-muted-foreground">·</span>
                    <span className="font-medium">{entry.changedBy}</span>
                    <span className="text-muted-foreground">changed</span>
                    <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">
                      {PROOF_POLICY_LABEL[entry.from]}
                    </Badge>
                    <span className="text-muted-foreground">→</span>
                    <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">
                      {PROOF_POLICY_LABEL[entry.to]}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <PhaseSubmitGapLog />
      </CardContent>
    </Card>
  );
}

function PhaseSubmitGapLog() {
  const exec = useInstallerExecution();
  const [open, setOpen] = useState(false);
  const events = exec.proofGapEvents;

  if (events.length === 0) return null;

  return (
    <div className="pt-2 border-t">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen((s) => !s)}
        className="h-7 px-2 gap-1.5 text-[11px]"
      >
        <AlertTriangle className="h-3 w-3" />
        {open ? "Hide" : "Show"} phase submit gaps ({events.length})
      </Button>
      {open && (
        <div className="mt-2 rounded border overflow-hidden">
          <div className="grid grid-cols-[110px_1fr_90px_1fr_90px] gap-2 px-2 py-1.5 bg-muted/60 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <div>Time</div>
            <div>Job · Phase</div>
            <div>Installer</div>
            <div>Missing</div>
            <div>Policy</div>
          </div>
          <div className="max-h-64 overflow-y-auto divide-y">
            {events.map((ev) => {
              const missingSummary = [
                ev.missingPhotos.length > 0
                  ? `${ev.missingPhotos.length} photo${ev.missingPhotos.length !== 1 ? "s" : ""}`
                  : null,
                ev.missingChecklist.length > 0
                  ? `${ev.missingChecklist.length} check${ev.missingChecklist.length !== 1 ? "s" : ""}`
                  : null,
              ].filter(Boolean).join(", ");
              return (
                <div
                  key={ev.id}
                  className="grid grid-cols-[110px_1fr_90px_1fr_90px] gap-2 px-2 py-1.5 text-[11px] items-center"
                >
                  <span className="text-muted-foreground tabular-nums">
                    {new Date(ev.capturedAt).toLocaleString("en-US", {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </span>
                  <span className="font-medium truncate" title={`${ev.jobLabel} · ${ev.phaseLabel}`}>
                    {ev.jobLabel} · <span className="text-muted-foreground">{ev.phaseLabel}</span>
                  </span>
                  <span className="truncate" title={ev.installer}>{ev.installer}</span>
                  <span className="text-warning font-medium">{missingSummary}</span>
                  <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 justify-self-start">
                    {PROOF_POLICY_LABEL[ev.policyAtSubmit]}
                  </Badge>
                </div>
              );
            })}
          </div>
          {events.length >= 50 && (
            <div className="px-2 py-1 text-[10px] text-muted-foreground bg-muted/30 border-t">
              Showing last 50. Older entries roll off automatically.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
